import { queryClient } from '~/api/queryClient';
import { queryKeys } from '~/api/queryKeys';
import { fetchCurrentUser } from '~/features/auth/services/auth.service';
import { useAuthStore } from '~/store/auth.store';

/**
 * Re-fetch /auth/me and mirror the result into the Zustand auth store so any
 * UI bound to the store (TenantSwitcher, header, permission gates) reflects
 * membership/profile changes without a full page reload.
 *
 * Why this helper exists: `accessibleTenants` lives on `auth.store` and is only
 * written by `setUser`. `invalidateQueries(auth.me)` alone is a no-op because
 * no component subscribes to that key via `useQuery` — bootstrap uses
 * `fetchQuery`. Call this from mutation `onSuccess` whenever the mutation can
 * change the user's tenant membership (create / update / delete / status).
 *
 * Note: bypasses the query cache with `staleTime: 0` — the global default is
 * 5 minutes, which would otherwise return the stale bootstrap snapshot
 * instead of hitting the network.
 */
export async function refreshAuthUser(): Promise<void> {
  const user = await queryClient.fetchQuery({
    queryKey: queryKeys.auth.me(),
    queryFn: fetchCurrentUser,
    staleTime: 0,
  });
  if (user) {
    useAuthStore.getState().setUser(user);
  }
}
